import { Radio, Plus, Users } from "lucide-react"
import type { Broadcast } from "./types"
import { AvatarPhoto } from "./avatar-photo"

/**
 * Broadcast-lists tab. Rows come from `sdk.broadcasts` via `useBroadcasts`,
 * owned by PresencePage (which also owns the recipient picker used to create
 * one) — this component is presentational.
 */
interface BroadcastTabProps {
  broadcasts: Broadcast[]
  isLoading: boolean
  error: string | null
  selectedId: string | null
  onSelect: (id: string) => void
  onCreate: () => void
}

export function BroadcastTab({ broadcasts, isLoading, error, selectedId, onSelect, onCreate }: BroadcastTabProps) {
  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <h2 className="text-base font-semibold text-foreground">Broadcast</h2>
        <button className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="New broadcast" onClick={onCreate}>
          <Plus className="w-4.5 h-4.5" />
        </button>
      </div>

      {/* Broadcast list */}
      <div className="flex-1 overflow-y-auto">
        {error ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
            <p className="text-sm font-medium text-foreground mb-1">Couldn't load broadcasts</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        ) : isLoading && broadcasts.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-muted-foreground">Loading…</p>
          </div>
        ) : broadcasts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
            <Radio className="w-12 h-12 text-muted-foreground/30 mb-4" strokeWidth={1} />
            <p className="text-sm font-medium text-foreground mb-1">No broadcasts yet</p>
            <p className="text-xs text-muted-foreground">Create a broadcast to send messages to multiple contacts at once.</p>
          </div>
        ) : (
          broadcasts.map(bc => (
            <div
              key={bc.id}
              role="button"
              onClick={() => onSelect(bc.id)}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                ${selectedId === bc.id ? "bg-primary/8" : "hover:bg-muted/50"}`}
            >
              {/* The broadcast's picture, or the broadcast icon without one */}
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                <AvatarPhoto
                  src={bc.imageUrl}
                  alt={bc.name}
                  className="w-full h-full object-cover"
                  fallback={<Radio className="w-5 h-5 text-primary" />}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  {/* `truncate` keeps the row one line, so a name near the
                      100-character maximum the create form allows is cut off
                      with no way to read the rest. The tooltip is that way —
                      the row cannot grow without breaking the list rhythm. */}
                  <p className="text-sm font-semibold text-foreground truncate" title={bc.name}>{bc.name}</p>
                  <span className="text-[11px] text-muted-foreground shrink-0">{bc.time}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p className="text-xs text-muted-foreground truncate">{bc.lastMessage}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    <Users className="w-3 h-3 text-muted-foreground/60" />
                    <span className="text-[11px] text-muted-foreground">{bc.recipientCount}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
